import {zodResolver} from '@hookform/resolvers/zod';
import {useQuery} from '@tanstack/react-query';
import type {ColumnDef, RowSelectionState} from '@tanstack/react-table';
import {functionalUpdate} from '@tanstack/react-table';
import {useMemo, useRef, useState} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {useReactToPrint} from 'react-to-print';

import tableSelectCol from '@/components/data-table/cols/tableSelectCol';
import {useTable} from '@/components/data-table/useTable';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {Separator} from '@/components/ui/separator';
import {Typography} from '@/components/ui/typography';
import {useToast} from '@/components/ui/use-toast';

import {useExportDefinitions} from '../hooks/useExportDefinitions';
import {downloadFile} from '../utils/downloadFile';
import type {ExportFormValues} from '../utils/exportFormInputs';
import {
  createExportFormSchema,
  exportFormDefaultValues,
} from '../utils/exportFormInputs';
import {buildClipboardText, buildCsvFile} from '../utils/exportOutputs';
import {EXPORT_PARAM} from '../utils/exportParams';
import {exportRegistry} from '../utils/exportRegistry';
import {getColumnFilterOptions} from '../utils/getColumnFilterOptions';
import ExportFormFields from './ExportFormFields';
import ExportPreview from './ExportPreview';
import ExportPrintDocument from './ExportPrintDocument';

export type ExportDialogProps = DialogProps;

const ExportDialog = ({onOpenChange, ...props}: ExportDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const definitions = useExportDefinitions();
  const definition = definitions.find(
    ({id}) => id === searchParams.get(EXPORT_PARAM)
  );

  const form = useForm<ExportFormValues>({
    resolver: zodResolver(createExportFormSchema(definition)),
    defaultValues: exportFormDefaultValues(searchParams, definition),
  });
  const [format, options] = useWatch({
    control: form.control,
    name: ['format', 'options'],
  });

  const listId = definition?.id;
  const parsedOptions = definition?.optionsSchema.safeParse(options);
  const validOptions = parsedOptions?.success ? parsedOptions.data : undefined;
  const recordsQuery = useQuery({
    queryKey: ['export', listId, validOptions],
    queryFn: () =>
      exportRegistry.find(({id}) => id === listId)?.load(validOptions ?? {}) ??
      [],
    enabled: !!validOptions,
  });
  const records = useMemo(() => recordsQuery.data ?? [], [recordsQuery.data]);

  const previewColumns = useMemo<ColumnDef<unknown>[]>(
    () => [
      tableSelectCol<unknown>(),
      ...(definition?.columns ?? []).map(column => ({
        id: column.header,
        header: column.header,
        accessorFn: (record: unknown) => column.value(record),
        enableSorting: false,
        ...(column.filterable && {
          filterFn: 'equalsString' as const,
          meta: {
            filter: {
              type: 'select' as const,
              options: getColumnFilterOptions(column.header),
            },
          },
        }),
      })),
    ],
    [definition]
  );
  const [rowSelection, setRowSelection] = useState<{
    dataUpdatedAt: number;
    rows: RowSelectionState;
  }>({dataUpdatedAt: 0, rows: {}});
  const currentRowSelection =
    rowSelection.dataUpdatedAt === recordsQuery.dataUpdatedAt
      ? rowSelection.rows
      : {};

  const table = useTable({
    data: records,
    columns: previewColumns,
    state: {
      rowSelection: currentRowSelection,
    },
    onRowSelectionChange: updater =>
      setRowSelection({
        dataUpdatedAt: recordsQuery.dataUpdatedAt,
        rows: functionalUpdate(updater, currentRowSelection),
      }),
    initialState: {
      pagination: {pageSize: 10},
      columnVisibility: Object.fromEntries(
        (definition?.columns ?? []).map(column => [
          column.header,
          !column.defaultHidden,
        ])
      ),
    },
  });

  const selectedColumns = (definition?.columns ?? []).filter(
    column => table.getColumn(column.header)?.getIsVisible()
  );

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const exportRecords = (
    selectedRows.length ? selectedRows : table.getFilteredRowModel().rows
  ).map(row => row.original);

  const printRef = useRef<HTMLDivElement>(null);
  const print = useReactToPrint({
    contentRef: printRef,
    documentTitle: definition?.getFileName(options),
    onAfterPrint: () => onOpenChange?.(false),
  });

  const handleExport = async (values: ExportFormValues) => {
    if (!definition) return;
    if (values.format === 'pdf') {
      print();
      return;
    }
    if (values.format === 'copy') {
      await navigator.clipboard.writeText(
        buildClipboardText(exportRecords, selectedColumns)
      );
      toast({title: t('Copied to clipboard'), variant: 'success'});
      onOpenChange?.(false);
      return;
    }
    downloadFile(
      buildCsvFile(definition, exportRecords, values.options, selectedColumns),
      `${definition.getFileName(values.options)}.csv`
    );
    onOpenChange?.(false);
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild className="bg-white md:max-w-6xl">
          <form onSubmit={form.handleSubmit(handleExport)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Export data')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              <ExportFormFields
                form={form}
                definitions={definitions}
                definition={definition}
              />
              {definition && (
                <>
                  <Separator className="my-3" />
                  <section className="flex flex-col gap-6">
                    <Typography variant="h4" asChild>
                      <h2>{t('Export contents')}</h2>
                    </Typography>
                    <ExportPreview
                      table={table}
                      isLoading={recordsQuery.isLoading}
                      exportCount={exportRecords.length}
                    />
                  </section>
                </>
              )}
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange?.(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!exportRecords.length || !selectedColumns.length}
              >
                {t('Export')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {definition && format === 'pdf' && (
        <div className="hidden">
          <ExportPrintDocument
            ref={printRef}
            definition={definition}
            records={exportRecords}
            options={options}
            columns={selectedColumns}
          />
        </div>
      )}
    </Form>
  );
};

export default ExportDialog;
